import { useOrdersSignalR } from '@/features/orders/hooks/useOrdersSignalR';
import { CONVERSATIONS_READ_PERMISSION, useConversationsAccess } from '../hooks/useConversationsAccess';
import { useConversationsSummary, useCustomerAgentStatus } from '../hooks/useConversations';
import { playHandoffAlert } from './handoff-alert-sound';
import { reasonLabel } from './reason-labels';
import { useTick } from './use-media-query';
import { elapsedMs, formatWaitPhrase, serverNow } from './wait-time';
import type { ConversationSummary } from '../types/conversations.types';

/* ==========================================================================
   Fontes dos alertas globais (change customer-agent-handoff, D19)

   O `ConversationAlerts` não é amarrado ao resumo de conversas: ele percorre
   esta lista, e cada fonte diz quantas pendências há, qual é a mais nova e o
   que escrever na faixa e na notificação (já sem nome, telefone ou texto do
   cliente). A aba líder, o som, o silêncio, o título e a notificação são do
   `ConversationAlerts` e valem para todas: o título soma as contagens das
   fontes visíveis, o som repete enquanto alguma tem contagem, e cada fonte
   tem a sua linha na faixa.

   Fonte nova (as solicitações de pedido do cliente, por exemplo) entra aqui,
   em `ALERT_SOURCES`, com a permissão de quem a vê. Nunca um segundo
   `ConversationAlerts`: dois tocariam em dobro e disputariam o título. A
   disponibilidade da área (`useCustomerAgentAvailable` com a permissão da
   fonte) vale para todas, porque todas são do Atendente IA.
   ========================================================================== */

export interface AlertSourceLink {
  to: string;
  label: string;
}

export interface AlertSourceNotification {
  title: string;
  /** Só o motivo e a prioridade: nunca nome, telefone ou texto. */
  body: string;
  /** Uma notificação por pendência (o navegador troca a de mesma `tag`). */
  tag: string;
  /** Para onde o clique leva. */
  href: string;
}

export interface AlertSourceState {
  /** Pendências que abrem a linha da faixa, o título e o som; 0 esconde a linha. */
  count: number;
  highPriorityCount: number;
  /**
   * Marca monotônica da pendência mais nova (o instante do alerta, em ms): há
   * pendência nova (toca e notifica) só quando ela passa a maior já vista pela
   * fonte. Resolver a mais nova, com outra mais antiga ainda pendente, faz a
   * marca voltar para a da antiga, e isso não é pendência nova.
   */
  latestMark: number | null;
  /** A linha da faixa. */
  line: string;
  /** Explicação acrescentada à linha (o atendente desligado, por exemplo). */
  note: string | null;
  link: AlertSourceLink;
  notification: AlertSourceNotification | null;
}

export interface AlertSource {
  id: string;
  /** A claim que vê a fonte; sem ela, a fonte nem é consultada. */
  permission: string;
  /**
   * O estado da fonte, ou nulo enquanto ela não sabe (carregando) ou quando
   * ela se esconde em silêncio (o 403 de token antigo). Só é chamado com a
   * permissão e a área disponíveis.
   */
  useAlertState: () => AlertSourceState | null;
  /** O som da pendência; o do atendimento, por padrão. Devolve se tocou. */
  sound?: () => boolean;
}

/* ---- Fonte `conversations`: clientes esperando a equipe ---- */

export const CONVERSATIONS_ALERT_LINK: AlertSourceLink = { to: '/conversations', label: 'Ver conversas' };

export const ATTENDANT_OFF_ALERT_NOTE =
  'O Atendente IA está desligado. Responda pelo WhatsApp do celular e clique Assumir para parar o aviso.';

export const HANDOFF_NOTIFICATION_TITLE = 'Cliente esperando atendimento';

/**
 * "1 cliente esperando atendimento no WhatsApp · há 3 min" ou "3 clientes
 * esperando atendimento no WhatsApp · o mais antigo há 12 min", com
 * "· 1 prioritário" quando há alta prioridade. A espera é contada do alerta
 * mais antigo contra o relógio do servidor.
 */
export function conversationsAlertLine(summary: ConversationSummary, now: number): string {
  const wait = formatWaitPhrase(elapsedMs(summary.oldestAlertedAt, now));
  const parts =
    summary.awaiting === 1
      ? [`1 cliente esperando atendimento no WhatsApp`, wait]
      : [`${summary.awaiting} clientes esperando atendimento no WhatsApp`, `o mais antigo ${wait}`];
  if (summary.awaitingHighPriority > 0) {
    parts.push(`${summary.awaitingHighPriority} ${summary.awaitingHighPriority === 1 ? 'prioritário' : 'prioritários'}`);
  }
  return parts.join(' · ');
}

/** "Motivo: Alergia grave · Prioritário": só o motivo e a prioridade. */
export function handoffNotificationBody(reason: string, priority: string): string {
  return `Motivo: ${reasonLabel(reason)}${priority === 'high' ? ' · Prioritário' : ''}`;
}

/** O estado da fonte a partir do resumo; só as conversas alertadas contam (a adiada não abre faixa). */
export function conversationsAlertState(
  summary: ConversationSummary,
  now: number,
  attendantDisabled: boolean,
): AlertSourceState {
  const latest = summary.awaiting > 0 ? summary.latestAlerted : null;
  const latestAt = latest ? Date.parse(latest.alertedAt) : Number.NaN;
  return {
    count: summary.awaiting,
    highPriorityCount: summary.awaitingHighPriority,
    latestMark: Number.isFinite(latestAt) ? latestAt : null,
    line: summary.awaiting > 0 ? conversationsAlertLine(summary, now) : '',
    note: summary.awaiting > 0 && attendantDisabled ? ATTENDANT_OFF_ALERT_NOTE : null,
    link: CONVERSATIONS_ALERT_LINK,
    notification: latest
      ? {
          title: HANDOFF_NOTIFICATION_TITLE,
          body: handoffNotificationBody(latest.reason, latest.priority),
          tag: `conversation-${latest.conversationId}`,
          href: `/conversations/${encodeURIComponent(latest.conversationId)}`,
        }
      : null,
  };
}

function useConversationsAlertState(): AlertSourceState | null {
  // A conexão de tempo real é a compartilhada de pedidos: as campainhas de
  // conversa revalidam o resumo, e a faixa a mantém aberta em qualquer tela.
  useOrdersSignalR();
  // Recontar "há N min" sem nova leitura.
  useTick(30_000);
  const { available } = useConversationsAccess();
  const summary = useConversationsSummary();
  const status = useCustomerAgentStatus();
  if (!available || !summary.data) return null;
  const now = serverNow({ serverTime: summary.data.serverTime, receivedAt: summary.dataUpdatedAt });
  return conversationsAlertState(summary.data, now, status.data?.state === 'disabled');
}

export const CONVERSATIONS_ALERT_SOURCE: AlertSource = {
  id: 'conversations',
  permission: CONVERSATIONS_READ_PERMISSION,
  useAlertState: useConversationsAlertState,
  sound: playHandoffAlert,
};

/** As fontes registradas, na ordem das linhas da faixa. */
export const ALERT_SOURCES: readonly AlertSource[] = [CONVERSATIONS_ALERT_SOURCE];
